import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type {
  Session,
  SessionManager,
} from "@mirage/analytics/session/session-manager";
import type { SearchSessionEndReason } from "@mirage/analytics/session/session-utils";
import { SEARCH_SESSION_END_REASONS } from "@mirage/analytics/session/session-utils";
import { tagged } from "@mirage/service-logging";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import { useCallback } from "react";

const logger = tagged("useCloseOmniboxSearchSession");

type RestrictedFeatureLine = "assist" | "stacks";

type Properties<T extends RestrictedFeatureLine> = T extends "assist"
  ? {
      conversationId?: string;
    }
  : {
      stackId?: string;
    };

export const useCloseOmniboxSearchSession = () => {
  const { searchSessionManager } = useMirageAnalyticsContext();

  const closeOmniboxSearchSessionIfExists = useCallback(
    <T extends RestrictedFeatureLine>(
      featureLine: T,
      properties: Properties<T>,
    ) => {
      logger.debug(
        `Attempting to close Omnibox Search Session on ${featureLine}`,
      );

      const session = searchSessionManager.getSession();

      if (!shouldPerformClose(featureLine, properties, session)) return;

      searchSessionManager.updateProperties({
        ...properties,
        featureLine,
      });
      searchSessionManager.endSession(getSearchAttemptEndReason(featureLine));

      logger.debug(
        `Successfully closed Omnibox Search Session on ${featureLine}`,
      );
    },
    [searchSessionManager],
  );

  return { closeOmniboxSearchSessionIfExists };
};

export function getSearchAttemptEndReason(
  featureLine: RestrictedFeatureLine,
): SearchSessionEndReason {
  switch (featureLine) {
    case "assist":
      return SEARCH_SESSION_END_REASONS.REGISTER_ASSIST_CREATION;
    case "stacks":
      return SEARCH_SESSION_END_REASONS.REGISTER_STACK_CREATION;
    default:
      featureLine satisfies never;
      throw new Error("Invalid feature line");
  }
}

export function shouldPerformClose(
  featureLine: RestrictedFeatureLine,
  properties: Properties<RestrictedFeatureLine>,
  session: Session | null,
): boolean {
  if (!session) return false;

  switch (featureLine) {
    case "stacks":
      if ("stackId" in properties) {
        return !!properties.stackId?.length;
      }
      break;
    case "assist":
      if ("conversationId" in properties) {
        return !!properties.conversationId?.length;
      }
      break;
    default: {
      featureLine satisfies never;
      return false;
    }
  }

  return false;
}

export function getPapTrackOmniboxFocusMode(
  isOmniboxEnabled: boolean,
  omniboxFocusMode: typeahead.Mode,
  searchSessionManager: SessionManager,
) {
  return () => {
    if (
      !isOmniboxEnabled ||
      !omniboxFocusMode ||
      !searchSessionManager.getSessionIdOrUndefined()
    )
      return;

    updateSearchSessionModeAndFeatureLine(
      omniboxFocusMode,
      searchSessionManager,
    );
  };
}

export function updateSearchSessionModeAndFeatureLine(
  omniboxFocusMode: typeahead.Mode,
  searchSessionManager: SessionManager,
) {
  searchSessionManager.extendOrCreateSession();
  searchSessionManager.updateProperties({
    omniboxFocusMode,
    featureLine: getFeatureLineByMode(omniboxFocusMode),
  });
}

export function getFeatureLineByMode(omniboxFocusMode: typeahead.Mode) {
  switch (omniboxFocusMode) {
    case typeahead.Mode.Default:
      return "omnibox";
    case typeahead.Mode.Find:
      return "search";
    case typeahead.Mode.Ask:
    case typeahead.Mode.Write:
      return "assist";
    case typeahead.Mode.Organize:
      return "stacks";
    default:
      omniboxFocusMode satisfies never;
  }
}
